/**
 * SurfaceErrorBoundary — Surface Layer (/01)
 *
 * Contains render errors so one failing view never blanks the whole shell.
 * Renders a Carbon error state with a reload action and records an
 * `error.boundary` telemetry event (PII-safe: message + route only).
 */

import { Component, type ReactNode } from 'react';
import { Button, InlineNotification } from '@carbon/react';
import { track } from '@lib/telemetry';
import '../styles/views.scss';

interface SurfaceErrorBoundaryProps {
  children: ReactNode;
}

interface SurfaceErrorBoundaryState {
  hasError: boolean;
  message: string;
}

export class SurfaceErrorBoundary extends Component<SurfaceErrorBoundaryProps, SurfaceErrorBoundaryState> {
  constructor(props: SurfaceErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: unknown): SurfaceErrorBoundaryState {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : 'Unexpected error',
    };
  }

  override componentDidCatch(error: unknown): void {
    track('error.boundary', {
      message: (error instanceof Error ? error.message : 'unknown').slice(0, 200),
      route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });
  }

  private readonly handleReload = (): void => {
    this.setState({ hasError: false, message: '' });
    if (typeof window !== 'undefined') window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="view">
          <header className="view__header">
            <h1 className="view__title">Something went wrong</h1>
            <p className="view__subtitle">This view hit an unexpected error and was contained.</p>
          </header>
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="View error"
            subtitle={this.state.message}
          />
          <div className="view-actions">
            <Button onClick={this.handleReload}>Reload</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
